import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Button,
  LayerCard,
  Badge,
  Text,
  Grid,
  GridItem,
  Empty,
} from '@cloudflare/kumo'
import {
  MapPin,
  Clock,
  Phone,
  EnvelopeSimple,
  NavigationArrow,
  House,
  UserPlus,
} from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { listGuestInvitations } from '../api/guestInvitations'
import { useTranslation } from '../i18n'
import { useHasRole } from '../auth/useRoles'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type {
  Building,
  Room,
  BuildingAmenity,
  AmenityCategory,
  GuestInvitationDto,
} from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface BuildingWithRooms extends Building {
  rooms: Room[]
}

interface MyBuildingResponse {
  building: BuildingWithRooms | null
}

type AuthMeResponse =
  | { status: 'no_account'; email: string; roles: string[] }
  | {
      status: 'active'
      coworker: { id: string; name: string; email: string; companyId: string | null }
      hasWifiCredential: boolean
      wifiUsername: string | null
      roles: string[]
    }

/* ------------------------------------------------------------------ */
/*  Day name helper                                                    */
/* ------------------------------------------------------------------ */

const DAY_KEYS = [
  'buildings.hours.sunday',
  'buildings.hours.monday',
  'buildings.hours.tuesday',
  'buildings.hours.wednesday',
  'buildings.hours.thursday',
  'buildings.hours.friday',
  'buildings.hours.saturday',
]

/* ------------------------------------------------------------------ */
/*  Category labels                                                    */
/* ------------------------------------------------------------------ */

const CATEGORY_KEYS: Record<AmenityCategory, string> = {
  connectivity: 'buildings.amenities.connectivity',
  food: 'buildings.amenities.food',
  transport: 'buildings.amenities.transport',
  accessibility: 'buildings.amenities.accessibility',
  facilities: 'buildings.amenities.facilities',
}

/* ------------------------------------------------------------------ */
/*  DashboardPage                                                      */
/* ------------------------------------------------------------------ */

export function DashboardPage() {
  const { getAccessTokenSilently, user } = useAuth0()
  const { t } = useTranslation()
  const isUser = useHasRole('user')
  const [building, setBuilding] = useState<BuildingWithRooms | null>(null)
  const [upcomingInvitations, setUpcomingInvitations] = useState<GuestInvitationDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  async function copyLink(url: string, key: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedToken(key)
      window.setTimeout(() => setCopiedToken(null), 2000)
    } catch {
      // Browser denied clipboard access — silently no-op; the URL is still
      // visible on the invitations page if the user opens it manually.
    }
  }

  useEffect(() => {
    async function load() {
      try {
        const token = await getAccessTokenSilently()

        // Fire login-callback to ensure coworker exists, then validate auth state.
        await apiRequest('/api/auth/login-callback', { method: 'POST', token }).catch(() => {})

        const me = await apiRequest<AuthMeResponse>('/api/auth/me', { token })
        if (me.status === 'no_account') {
          setError('NO_COWORKER')
          return
        }
        if (!me.coworker.companyId) {
          setError('NO_COMPANY')
          return
        }

        const [buildingRes, invitationsRes] = await Promise.all([
          apiRequest<MyBuildingResponse>('/api/my/building', { token }),
          // Only fetch invitations for users — operators share the same
          // dashboard but the upcoming-invitations card is user-only;
          // admins are routed to CompaniesPage by RoleBasedHome.
          isUser ? listGuestInvitations(token).catch(() => [] as GuestInvitationDto[]) : [],
        ])

        setBuilding(buildingRes.building)
        const todayIso = new Date().toISOString().slice(0, 10)
        setUpcomingInvitations(invitationsRes.filter((inv) => inv.visitDate >= todayIso))
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setError('NOT_ENROLLED')
        } else {
          setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
        }
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [getAccessTokenSilently, isUser])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size="lg" />
      </div>
    )
  }

  if (error) {
    const message =
      error === 'NO_COWORKER' || error === 'NOT_ENROLLED'
        ? t('dashboard.error.noCoworker')
        : error === 'NO_COMPANY'
          ? t('dashboard.error.noCompany')
          : t('common.error')
    const variant = error === 'GENERIC_ERROR' ? 'error' : 'alert'
    return <Banner variant={variant}>{message}</Banner>
  }

  if (!building) {
    return (
      <div className="py-8 text-center">
        <Text variant="secondary">{t('dashboard.noBuilding')}</Text>
      </div>
    )
  }

  const userName = user?.name ?? user?.email ?? ''
  const welcomeText = t('dashboard.welcome')
    .replace('{name}', userName.split(' ')[0] ?? userName)
    .replace('{building}', building.name)

  const sortedHours = [...building.hours].sort((a, b) => {
    // Monday first: 1,2,3,4,5,6,0
    const orderA = a.dayOfWeek === 0 ? 7 : a.dayOfWeek
    const orderB = b.dayOfWeek === 0 ? 7 : b.dayOfWeek
    return orderA - orderB
  })

  const amenitiesByCategory: Record<string, BuildingAmenity[]> = {}
  for (const amenity of building.amenities) {
    if (!amenitiesByCategory[amenity.category]) {
      amenitiesByCategory[amenity.category] = []
    }
    amenitiesByCategory[amenity.category]!.push(amenity)
  }

  return (
    <>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Current icon={<House size={14} />}>{t('nav.home')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('nav.home')}
        description={welcomeText}
      />

      <Grid variant="2-1" gap="base">
        {/* Main Column */}
        <GridItem>
          <div className="flex flex-col gap-6">
            {/* Welcome Banner */}
            {building.imageUrl && (
              <div className="relative rounded-xl overflow-hidden" style={{ height: 200 }}>
                <img
                  src={building.imageUrl}
                  alt={building.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <Text variant="heading2" as="h2">
                    {welcomeText}
                  </Text>
                  <Text as="p" size="sm" DANGEROUS_className="opacity-80">
                    {new Date().toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Text>
                </div>
              </div>
            )}

            {/* Space Highlights */}
            {building.photos.length > 0 && (
              <div>
                <div className="mb-3">
                  <Text variant="heading2">{t('dashboard.spaceHighlights')}</Text>
                </div>
                <Grid variant="3up" gap="base">
                  {building.photos
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((photo, index) => (
                      <GridItem key={index}>
                        <div className="relative rounded-lg overflow-hidden">
                          <img
                            src={photo.url}
                            alt={photo.caption}
                            className="w-full h-40 object-cover"
                          />
                          {photo.caption && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                              <span className="text-xs text-white">{photo.caption}</span>
                            </div>
                          )}
                        </div>
                      </GridItem>
                    ))}
                </Grid>
              </div>
            )}

            {/* Building Information */}
            <LayerCard>
              <LayerCard.Primary>
                <div className="mb-3">
                  <Text variant="heading2">{t('dashboard.buildingInfo')}</Text>
                </div>

                {building.description && (
                  <div className="mb-4">
                    <Text size="sm" variant="secondary">
                      {building.description}
                    </Text>
                  </div>
                )}

                <div className="flex items-start gap-2 mb-4">
                  <MapPin size={16} className="text-kumo-subtle mt-0.5" />
                  <Text size="sm">{building.address}</Text>
                </div>

                {/* Operating Hours */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={16} className="text-kumo-subtle" />
                    <span className="text-sm font-medium">{t('dashboard.operatingHours')}</span>
                  </div>
                  <div className="flex flex-col gap-1 pl-6">
                    {sortedHours.map((hour) => (
                      <div
                        key={hour.dayOfWeek}
                        className={`flex justify-between text-sm ${hour.isClosed ? 'text-kumo-muted' : ''}`}
                      >
                        <span>{t(DAY_KEYS[hour.dayOfWeek] ?? '')}</span>
                        <span>
                          {hour.isClosed
                            ? t('buildings.hours.closed')
                            : `${hour.openTime} - ${hour.closeTime}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Google Maps Link */}
                {building.googleMapsUrl && (
                  <a
                    href={building.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-kumo-default hover:underline"
                  >
                    <NavigationArrow size={16} />
                    {t('dashboard.openInMaps')}
                  </a>
                )}
              </LayerCard.Primary>
            </LayerCard>

            {/* Contact */}
            {(building.phone || building.email) && (
              <LayerCard>
                <LayerCard.Primary>
                  <div className="mb-3">
                    <Text variant="heading2">{t('dashboard.contact')}</Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    {building.phone && (
                      <a
                        href={`tel:${building.phone}`}
                        className="flex items-center gap-2 text-sm text-kumo-default hover:underline"
                      >
                        <Phone size={16} className="text-kumo-subtle" />
                        {building.phone}
                      </a>
                    )}
                    {building.email && (
                      <a
                        href={`mailto:${building.email}`}
                        className="flex items-center gap-2 text-sm text-kumo-default hover:underline"
                      >
                        <EnvelopeSimple size={16} className="text-kumo-subtle" />
                        {building.email}
                      </a>
                    )}
                  </div>
                </LayerCard.Primary>
              </LayerCard>
            )}

            {/* Amenities */}
            {Object.keys(amenitiesByCategory).length > 0 && (
              <LayerCard>
                <LayerCard.Primary>
                  <div className="mb-3">
                    <Text variant="heading2">{t('buildings.amenities')}</Text>
                  </div>
                  {(Object.entries(amenitiesByCategory) as [string, BuildingAmenity[]][]).map(
                    ([category, items]) => (
                      <div key={category} className="mb-3">
                        <span className="text-xs text-kumo-subtle font-medium mb-1.5 block">
                          {t(CATEGORY_KEYS[category as AmenityCategory] ?? category)}
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {items.map((amenity) => (
                            <Badge key={amenity.name} variant="secondary">
                              {amenity.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </LayerCard.Primary>
              </LayerCard>
            )}
          </div>
        </GridItem>

        {/* Sidebar */}
        <GridItem>
          <div className="flex flex-col gap-4">
            {/* Upcoming Guest Invitations */}
            {isUser && (
              <LayerCard>
                <LayerCard.Primary>
                  <Text variant="heading2" as="h2">
                    {t('dashboard.upcomingInvitations')}
                  </Text>

                  {upcomingInvitations.length === 0 ? (
                    <div className="flex flex-col gap-2 items-center">
                      <Empty
                        title={t('dashboard.upcomingInvitations.empty')}
                        description={t('dashboard.upcomingInvitations.emptyDescription')}
                      />
                      <Link
                        to="/guest-invitations"
                        className="text-sm font-medium text-kumo-default hover:underline"
                      >
                        {t('dashboard.upcomingInvitations.cta')}
                      </Link>
                    </div>
                  ) : (
                    <ul className="flex flex-col gap-1.5">
                      {upcomingInvitations.slice(0, 5).map((inv) => (
                        <li
                          key={inv.id}
                          className="flex items-center justify-between gap-2 rounded-md border border-kumo-line px-3 py-2"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">
                              {inv.guestName}
                              {inv.singleAccess && (
                                <Badge className="ml-2" variant="secondary">
                                  1
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs opacity-70">
                              {inv.visitDate} · {inv.visitTimeBegin}–{inv.visitTimeEnd}
                            </div>
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="shrink-0"
                            onClick={() => copyLink(inv.invitationUrl, inv.id)}
                          >
                            {copiedToken === inv.id
                              ? t('guestInvitations.success.copied')
                              : t('guestInvitations.list.copyLink')}
                          </Button>
                        </li>
                      ))}
                      {upcomingInvitations.length > 5 && (
                        <li className="pt-1">
                          <Link
                            to="/guest-invitations"
                            className="text-sm font-medium text-kumo-default hover:underline"
                          >
                            {t('dashboard.upcomingInvitations.seeAll').replace(
                              '{count}',
                              String(upcomingInvitations.length),
                            )}
                          </Link>
                        </li>
                      )}
                    </ul>
                  )}
                </LayerCard.Primary>
              </LayerCard>
            )}
          </div>
        </GridItem>
      </Grid>
    </>
  )
}
